// Editorial Cream — port of PostEditorial from the Claude Design bundle.
//
// Renders at a fixed 1080×1080 reference size. Parent (StepSocialFinal)
// wraps this in a scaled container via CSS transform so it fits the
// viewport while remaining pixel-perfect for PNG export via html-to-image.
//
// Key prop: `slots` carries the 10 editable copy fields (eyebrow, headline,
// subhead, bullet1-3, cta, handle, badge_stars, badge_text). All come from
// /api/suggest-copy?format=social-post-12-slot or from manual edits in the
// Tweaks Panel.

import type { SocialPostSlots } from '@/lib/api';
import './PostEditorial.css';

interface PostEditorialProps {
  slots: SocialPostSlots;
  /** Data URL or blob URL for the user's product photo. Renders inside the
   *  `.product` slot. Empty string → empty-state placeholder. */
  productImageUrl: string;
}

export default function PostEditorial({ slots, productImageUrl }: PostEditorialProps) {
  return (
    <div className="post-editorial">
      <div className="frame-pad">
        <div className="eyebrow">{slots.eyebrow}</div>
        <div className="grid">
          <div className="left">
            {/* dangerouslySetInnerHTML — the AI is constrained to a single
                <em>...</em> in headline copy. We trust GPT-4o-mini's output
                here because the prompt enforces the format strictly. If we
                ever want defense in depth, sanitize via DOMPurify. */}
            <h1 dangerouslySetInnerHTML={{ __html: slots.headline }} />
            <div className="sub">{slots.subhead}</div>
            <ul className="bullets">
              {/* Bullets render text content directly in the <li>; the
                  ::before pseudo-element in the CSS adds the brown dot
                  in the first grid column. Don't wrap in extra <span>s
                  — that pushes the text into the wrong grid track. */}
              <li>{slots.bullet1}</li>
              <li>{slots.bullet2}</li>
              <li>{slots.bullet3}</li>
            </ul>
          </div>
          <div className="right">
            <div className="product">
              {productImageUrl ? (
                <img src={productImageUrl} alt="Product" />
              ) : (
                <div className="empty-state">Product image</div>
              )}
            </div>
            <div className="meta">
              <div className="handle"><span>—</span> {slots.handle}</div>
              <div>{slots.issue_label || ''}</div>
            </div>
          </div>
        </div>
        <div className="footer">
          <div className="badge">
            <span className="stars">{slots.badge_stars}</span>
            <span className="num">{slots.badge_text}</span>
          </div>
          <div className="cta">
            {slots.cta}
            <span className="arr">→</span>
          </div>
        </div>
      </div>
    </div>
  );
}
